import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Camera-engine circuit breaker.
 *
 * The `wasm_gphoto2_v2` flag is a coarse, manual, per-EMAIL opt-in: it says "this
 * operator is in the v2 rollout". This breaker is the fine, automatic, per-BODY safety
 * net underneath it: when v2 actually fails on a specific camera body, that body drops
 * back to v1 on the very next connect — within one recovery, no operator toggle, no
 * waiting for someone to notice and flip the flag.
 *
 * Design decisions, all deliberate:
 * - **Keyed by body, not by email.** One bad Sony on a booth must not disable v2 on the
 *   same operator's seven working Canons. The key is the USB identity of the body
 *   (vendor:product:serial); a booth with no serial for a model shares one key across
 *   that model, which on a single booth is almost always the one physical camera.
 * - **It can only ever turn v2 OFF.** A trip forces v1; nothing here can put a body onto
 *   v2 that `pickWasmVariant` did not already choose. So it can never widen exposure.
 * - **Scoped to the build that failed.** Each record carries the camera-engine build id
 *   (the v2 wasm hash). A new v2 build — i.e. a fix — is a different id, so every tripped
 *   body automatically gets one fresh attempt on it. That is the "fix on the next
 *   release" loop, with no manual un-tripping.
 * - **Expires.** A trip older than the window is forgotten, so a one-off (a flaky cable
 *   that day) does not exile a body from v2 forever.
 *
 * Persisted, because the whole point is to survive the app restart that a crash-recovery
 * or an operator reboot triggers — a booth must not re-try the same broken engine on the
 * same body every morning.
 */

/** The v2 wasm build this renderer bundles. Injected at build time (vite define); the
 *  fallback keeps unit tests and any un-defined context working. Changing the v2 wasm
 *  changes this, which is exactly when tripped bodies should get a fresh chance. */
declare const __CAMERA_ENGINE_BUILD__: string
export const CAMERA_ENGINE_BUILD: string =
  typeof __CAMERA_ENGINE_BUILD__ === 'string' ? __CAMERA_ENGINE_BUILD__ : 'dev'

/** A trip is forgotten after this long, so a transient bad day is not a life sentence. */
export const BREAKER_TTL_MS = 7 * 24 * 60 * 60 * 1000

export interface CameraBodyIdentity {
  vendorId: number
  productId: number
  serialNumber?: string | null
}

export interface BreakerRecord {
  vendorId: number
  productId: number
  serialNumber: string | null
  /** The decoded library message (or best available) that tripped it. */
  reason: string
  /** The camera-engine build id at trip time; a different current id clears it. */
  buildId: string
  trippedAt: number
}

/** Stable per-body key. Serial-less bodies collapse to one key per model (fine on a
 *  single booth). */
export function cameraBodyKey(id: CameraBodyIdentity): string {
  const vid = id.vendorId.toString(16).padStart(4, '0')
  const pid = id.productId.toString(16).padStart(4, '0')
  return `${vid}:${pid}:${id.serialNumber || 'noserial'}`
}

interface BreakerState {
  records: Record<string, BreakerRecord>
  /**
   * Is v2 vetoed for this body right now? True only for a live trip on the CURRENT
   * build within the TTL. A record from an older build or past the TTL is treated as
   * absent (and pruned lazily on the next write).
   */
  isTripped: (id: CameraBodyIdentity, now?: number) => boolean
  /** Record a v2 failure on this body. No-op if already tripped on this build. */
  trip: (id: CameraBodyIdentity, reason: string, now?: number) => void
  /** Operator "retry v2" for one body. */
  reset: (id: CameraBodyIdentity) => void
  /** Operator "retry v2 everywhere" (Settings). */
  resetAll: () => void
  /** Live trips on the current build, for telemetry/UI. */
  active: (now?: number) => BreakerRecord[]
}

function isLive(rec: BreakerRecord | undefined, now: number): rec is BreakerRecord {
  return (
    !!rec && rec.buildId === CAMERA_ENGINE_BUILD && now - rec.trippedAt < BREAKER_TTL_MS
  )
}

export const useCameraEngineBreakerStore = create<BreakerState>()(
  persist(
    (set, get) => ({
      records: {},
      isTripped: (id, now = Date.now()): boolean => isLive(get().records[cameraBodyKey(id)], now),
      trip: (id, reason, now = Date.now()): void => {
        const key = cameraBodyKey(id)
        const existing = get().records[key]
        if (isLive(existing, now)) return // already tripped on this build — keep the first reason
        set((state) => ({
          records: {
            ...state.records,
            [key]: {
              vendorId: id.vendorId,
              productId: id.productId,
              serialNumber: id.serialNumber ?? null,
              reason,
              buildId: CAMERA_ENGINE_BUILD,
              trippedAt: now
            }
          }
        }))
      },
      reset: (id): void => {
        set((state) => {
          const next = { ...state.records }
          delete next[cameraBodyKey(id)]
          return { records: next }
        })
      },
      resetAll: (): void => {
        set({ records: {} })
      },
      active: (now = Date.now()): BreakerRecord[] =>
        Object.values(get().records).filter((r) => isLive(r, now))
    }),
    {
      name: 'camera-engine-breaker-v1',
      partialize: (state) => ({ records: state.records })
    }
  )
)

/** Compact summary for telemetry: only live trips on the current build. */
export function cameraEngineBreakerSummary(now = Date.now()): {
  buildId: string
  tripped: Array<{ body: string; reason: string; at: number }>
} {
  return {
    buildId: CAMERA_ENGINE_BUILD,
    tripped: useCameraEngineBreakerStore
      .getState()
      .active(now)
      .map((r) => ({
        body: cameraBodyKey(r),
        reason: r.reason,
        at: r.trippedAt
      }))
  }
}
